// src/MentionContactsTab.jsx — Settings → Shopify Contacts (was Mention Contacts; Eric, 2026-10-21 / 2026-10-27).
// 2026-10-27: each contact also has its Lightspeed store (suggested from its name or mailbox) and an FBM Emails
// switch: those contacts get the FBM Report's store emails for orders moved to that store.
// Shared Shopify staff accounts with no Operations Hub user (store mailboxes, "<City> Production") that an
// admin may offer in the @ popover of order comments. One toggle per mailbox covers every store where it is
// staff. Shopify's own mention notification is the only channel: no Inbox, no email from OpsHub.
// Spec: docs/superpowers/specs/2026-10-21-mention-contacts-design.md
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Banner, BlockStack, Box, Button, Card, Checkbox, InlineStack, Select, Spinner, Text, TextField } from '@shopify/polaris';
import { authenticatedFetch } from './AuthComponent';

const SECTIONS = [
    { key: 'store', title: 'Stores', blurb: 'Store mailboxes such as eatontown@soccerpost.com.' },
    { key: 'production', title: 'Production locations', blurb: 'The "<City> Production" accounts used by the production teams.' },
    { key: 'other', title: 'Other', blurb: 'Vendors, collaborators and personal accounts. Usually left off.' },
];

const td = (extra = {}) => ({ padding: '6px 10px', borderBottom: '1px solid var(--p-color-border-secondary, #f1f2f3)', verticalAlign: 'middle', ...extra });

function NameField({ contact, onSave }) {
    const [v, setV] = useState(contact.name);
    useEffect(() => { setV(contact.name); }, [contact.name]);
    return (
        <TextField label="Name" labelHidden value={v} onChange={setV} autoComplete="off"
            onBlur={() => { const n = v.trim(); if (n && n !== contact.name) onSave(n); else setV(contact.name); }} />
    );
}

export default function MentionContactsTab() {
    const [contacts, setContacts] = useState([]);
    const [groups, setGroups] = useState({ store: 'Store', production: 'Production', other: 'Contact' });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [query, setQuery] = useState('');
    const [shops, setShops] = useState([]);

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const r = await authenticatedFetch('/api/admin/mention-contacts?store=soccerpost');
            if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
            const d = await r.json();
            setContacts(d.contacts || []); if (d.groups) setGroups(d.groups); setShops(d.shops || []);
        } catch (e) { setError(String(e.message || e)); } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = useCallback(async (contact, patch) => {
        setError(null);
        try {
            const r = await authenticatedFetch('/api/admin/mention-contacts?store=soccerpost', {
                method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: contact.email, ...patch }) });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
            setContacts(prev => prev.map(c => c.email === contact.email ? { ...c, ...d.contact } : c));
        } catch (e) { setError(String(e.message || e)); }
    }, []);

    const refresh = async () => {
        setRefreshing(true); setError(null); setNotice(null);
        try {
            const r = await authenticatedFetch('/api/admin/mention-contacts/refresh?store=soccerpost', { method: 'POST' });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
            const skipped = (d.skipped || []).map(s => `${s.store} (${s.reason})`).join(', ');
            setNotice(`Refreshed ${(d.refreshed || []).length} store${(d.refreshed || []).length === 1 ? '' : 's'}${skipped ? `. Skipped: ${skipped}` : ''}.`);
            await load();
        } catch (e) { setError(String(e.message || e)); } finally { setRefreshing(false); }
    };

    const q = query.trim().toLowerCase();
    const bySection = useMemo(() => {
        const out = { store: [], production: [], other: [] };
        contacts.forEach(c => {
            if (q && !(c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q))) return;
            (out[c.group_key || c.suggested_group] || out.other).push(c);
        });
        return out;
    }, [contacts, q]);
    const groupOptions = SECTIONS.map(s => ({ label: groups[s.key] || s.title, value: s.key }));
    const onCount = contacts.filter(c => c.enabled).length;
    const fbmCount = contacts.filter(c => c.fbm_emails).length;
    const shopOptions = useMemo(() => [{ label: 'No store', value: '0' }, ...shops.map(sh => ({ label: sh.name, value: String(sh.shop_id) }))], [shops]);

    return (
        <BlockStack gap="400">
            <Card>
                <BlockStack gap="300">
                    <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                        <Text as="h2" variant="headingMd">Shopify Contacts</Text>
                        <InlineStack gap="200" blockAlign="center">
                            <Text as="span" tone="subdued">{onCount} mentionable · {fbmCount} FBM emails · {contacts.length} listed</Text>
                            <Button onClick={refresh} loading={refreshing}>Refresh from Shopify</Button>
                        </InlineStack>
                    </InlineStack>
                    <Text as="p" tone="subdued">
                        These are Shopify staff accounts with no Operations Hub user, such as a store's or a production team's shared
                        mailbox. <b>Mentionable</b> offers it in @mentions on every order of the stores it belongs to; Shopify notifies it.
                        <b> FBM Emails</b> sends it the FBM Report's store emails (Amazon orders moved to its Lightspeed store, repeated
                        every 24 hours until the label is bought). The Lightspeed store is filled in from the name or mailbox; change it if it is wrong.
                    </Text>
                    {error && <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>}
                    {notice && <Banner tone="info" onDismiss={() => setNotice(null)}>{notice}</Banner>}
                    <div style={{ maxWidth: 360 }}>
                        <TextField label="Search" labelHidden placeholder="Search by name or email" value={query} onChange={setQuery} clearButton onClearButtonClick={() => setQuery('')} autoComplete="off" />
                    </div>
                </BlockStack>
            </Card>
            {loading ? <Box padding="400"><InlineStack align="center"><Spinner size="small" /></InlineStack></Box> : SECTIONS.map(sec => (
                <Card key={sec.key}>
                    <BlockStack gap="200">
                        <InlineStack gap="200" blockAlign="center">
                            <Text as="h3" variant="headingSm">{sec.title}</Text>
                            <Text as="span" tone="subdued">{bySection[sec.key].length}</Text>
                        </InlineStack>
                        <Text as="p" tone="subdued" variant="bodySm">{sec.blurb}</Text>
                        {bySection[sec.key].length === 0 ? <Text as="p" tone="subdued">None{q ? ' match' : ''}.</Text> : (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                    <thead>
                                        <tr>{['Mentionable', 'FBM Emails', 'Name', 'Email', 'Lightspeed store', 'Shopify stores', 'Group'].map(h => (
                                            <th key={h} style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid var(--p-color-border, #ddd)', whiteSpace: 'nowrap' }}>{h}</th>))}</tr>
                                    </thead>
                                    <tbody>
                                        {bySection[sec.key].map(c => (
                                            <tr key={c.email} data-email={c.email}>
                                                <td style={td({ width: 110 })}>
                                                    <Checkbox label="Mentionable" labelHidden checked={!!c.enabled} onChange={(v) => save(c, { enabled: v })} />
                                                </td>
                                                <td style={td({ width: 100 })}>
                                                    <Checkbox label="FBM Emails" labelHidden checked={!!c.fbm_emails} disabled={!c.fbm_emails && !c.shop_id}
                                                        onChange={(v) => save(c, { fbm_emails: v })} />
                                                </td>
                                                <td style={td({ minWidth: 220 })}><NameField contact={c} onSave={(name) => save(c, { name })} /></td>
                                                <td style={td({ whiteSpace: 'nowrap', color: 'var(--p-color-text-secondary, #6d7175)' })}>{c.email}</td>
                                                <td style={td({ width: 190 })}>
                                                    <Select label="Lightspeed store" labelHidden options={shopOptions} value={String(c.shop_id || 0)}
                                                        helpText={c.lightspeed_shop_id == null && c.shop_id ? 'suggested' : undefined}
                                                        onChange={(v) => save(c, { lightspeed_shop_id: Number(v) })} />
                                                </td>
                                                <td style={td()}><InlineStack gap="100" wrap>{(c.stores || []).map(s => <Badge key={s} size="small">{s}</Badge>)}</InlineStack></td>
                                                <td style={td({ width: 160 })}>
                                                    <Select label="Group" labelHidden options={groupOptions} value={c.group_key || c.suggested_group} onChange={(v) => save(c, { group_key: v })} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </BlockStack>
                </Card>
            ))}
        </BlockStack>
    );
}
